import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ChartLayout, MethodologyContent, OverviewContent } from './ChartLayout';
import { AuthoredEquityPanel, AuthoredRelevancePanel } from '../../domains/narrative/narrativeSectionContent';
import { buildPublicSafetyAuthoredBundle } from '../../domains/narrative/peoplePublicSafetyNarrative';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import {
  loadCrimeSafetyBundle,
  buildCrimeSafetyOverviewNarrative,
  type CrimeSafetyBundle,
} from '../../services/crimeSafetyService';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export interface CrimeSafetySectionProps {
  location: string;
}


const CrimeSafetySection: React.FC<CrimeSafetySectionProps> = ({ location }) => {
  const [bundle, setBundle] = useState<CrimeSafetyBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<string>('overview');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCrimeSafetyBundle(location)
      .then((b) => { if (!cancelled) setBundle(b); })
      .catch(() => { if (!cancelled) setBundle(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [location]);

  // Scroll to this section once data is loaded if the URL hash targets it.
  // Runs after our own fetch completes so the layout is stable (not mid-loading-spinner).
  useEffect(() => {
    if (loading) return;
    if (window.location.hash !== '#chart-public-safety') return;
    const t = setTimeout(() => {
      const el = document.getElementById('chart-public-safety');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [loading]);

  const narrative = useMemo(
    () => (bundle ? buildCrimeSafetyOverviewNarrative(location, bundle) : ''),
    [location, bundle]
  );

  const authored = useMemo(
    () => buildPublicSafetyAuthoredBundle(location, bundle),
    [location, bundle]
  );

  // ── Line chart: rate over time ──────────────────────────────────────────────
  const lineData = useMemo(() => {
    if (!bundle?.hasData || !bundle.years.length) return null;
    return {
      labels: bundle.years.map(String),
      datasets: [
        {
          label: bundle.locationDisplayName || location,
          data: bundle.localRatePer100k,
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.12)',
          fill: true,
          tension: 0.2,
          spanGaps: true,
        },
        {
          label: 'California',
          data: bundle.caRatePer100k,
          borderColor: 'rgb(220, 38, 38)',
          backgroundColor: 'rgba(220, 38, 38, 0.08)',
          fill: true,
          tension: 0.2,
          spanGaps: true,
        },
      ],
    };
  }, [bundle, location]);

  const yearRange = useMemo(() => {
    if (!bundle?.years?.length) return '';
    const first = bundle.years[0];
    const last = bundle.years[bundle.years.length - 1];
    return first === last ? String(first) : `${first}–${last}`;
  }, [bundle]);

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const },
        title: {
          display: true,
          text: `Violent crime rate per 100,000 residents (${yearRange})`,
        },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
              const v = ctx.parsed.y;
              const lab = ctx.dataset.label || '';
              return v != null && !Number.isNaN(v)
                ? `${lab}: ${v.toFixed(0)} per 100k`
                : `${lab}: No data`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Incidents per 100,000 residents' },
        },
        x: { title: { display: true, text: 'Year' } },
      },
    }),
    [yearRange]
  );

  // ── Key metrics ─────────────────────────────────────────────────────────────
  const keyMetricsEl = loading || !bundle ? null : (
    <KeyMetrics>
      {bundle.latestYear != null && (
        <KeyMetric value={String(bundle.latestYear)} label="Latest data year" color="#1e3a8a" />
      )}
      {bundle.latestLocalRate != null && (
        <KeyMetric
          value={bundle.latestLocalRate.toFixed(0)}
          label={`${bundle.locationDisplayName || location} (per 100k)`}
          color="#1e3a8a"
        />
      )}
      {bundle.latestCARate != null && (
        <KeyMetric value={bundle.latestCARate.toFixed(0)} label="California avg (per 100k)" color="#059669" />
      )}
      {bundle.latestLocalPercentile != null && (
        <KeyMetric
          value={`${Math.round(100 - bundle.latestLocalPercentile)}th`}
          label="Public safety score (CA)"
          color={Math.round(100 - bundle.latestLocalPercentile) >= 50 ? '#10b981' : Math.round(100 - bundle.latestLocalPercentile) >= 25 ? '#d97706' : '#dc2626'}
        />
      )}
    </KeyMetrics>
  );

  // ── Chart body ──────────────────────────────────────────────────────────────
  const chartBody = (() => {
    if (loading) {
      return <p style={{ padding: 24, color: '#6b7280' }}>Loading public safety data…</p>;
    }
    if (!bundle?.hasData) {
      return (
        <p style={{ padding: 24, color: '#6b7280' }}>
          No violent crime rate data found for this location. Data covers California cities, counties, and the state
          (CA DOJ Open Justice, 1990–2024). Some small cities served only by county sheriffs may not have matching rows.
          {bundle?.fetchWarnings?.length ? ` Notes: ${bundle.fetchWarnings.join(' ')}` : ''}
        </p>
      );
    }
    if (!lineData) {
      return <p style={{ padding: 24, color: '#6b7280' }}>No time-series data available.</p>;
    }
    return (
      <div style={{ height: 360, position: 'relative' }}>
        <Line data={lineData} options={lineOptions} />
      </div>
    );
  })();

  return (
    <ChartLayout
      scrollAnchorId="chart-public-safety"
      title="Public Safety"
      chartType="line"
      keyMetrics={keyMetricsEl}
      chartContent={<div id="crime-safety-section-chart">{chartBody}</div>}
      overviewContent={
        <OverviewContent
          title="Overview"
          description={authored.overview || 'Reported violent crime statistics from California DOJ Open Justice.'}
        >
          {bundle?.fetchWarnings?.length ? (
            <p style={{ color: '#b45309', marginBottom: 12 }}>Notes: {bundle.fetchWarnings.join(' ')}</p>
          ) : null}
          {authored.keyInsights ? <p>{authored.keyInsights}</p> : <p>{narrative}</p>}
          <p style={{ marginTop: 12, fontSize: 14, color: '#4b5563' }}>
            Data source: California Department of Justice (DOJ) Open Justice — Crimes and Clearances (UCR Summary).
            Covers California cities, counties, and state{yearRange ? ` (${yearRange})` : ''}. Rates are per 100,000 residents.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        authored.relevance ? <AuthoredRelevancePanel bundle={authored} /> : undefined
      }
      equityContent={
        authored.equity ? <AuthoredEquityPanel bundle={authored} /> : undefined
      }
      methodologyContent={
        <MethodologyContent
          title="Methods &amp; data sources"
          description="California Department of Justice (DOJ) Open Justice — Crimes and Clearances with Arson (UCR Summary)."
          dataSources={[
            `\`crime_safety_place\` — city, county, and state violent crime rate per 100k and risk percentile${yearRange ? ` (${yearRange})` : ''}`,
            'Source: CA DOJ Open Justice — Crimes and Clearances with Arson (UCR Summary CSV)',
            'City rows: agency name → city name matching; violent crimes summed across all reporting agencies per city per year.',
            'County and state rows: all reporting agencies aggregated by county FIPS.',
          ]}
          methodology={[
            'Violent crime rate = (violent crime count ÷ population) × 100,000.',
            'Risk percentile (0–100) ranks the location among California peers by crime risk within each year; for Public Safety as a benefit, the score is inverted (100 − percentile) so higher = safer.',
            'California comparison uses the state-level row in the same table (geofips variants for state FIPS 6).',
            `Time-series chart shows all available years${yearRange ? ` (${yearRange})` : ''} for the selected location, alongside the California average.`,
          ]}
        />
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      onDownloadPNG={() => {
        const canvas = document.querySelector('#crime-safety-section-chart canvas') as HTMLCanvasElement;
        if (canvas) {
          const link = document.createElement('a');
          link.download = `${formatFilename(location, 'Crime_Safety_trend')}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      }}
      onDownloadCSV={() => {
        if (!bundle?.hasData) return;
        const rows: Record<string, string | number | null>[] = [];
        bundle.years.forEach((y, i) => {
          rows.push({
            location: bundle.locationDisplayName || location,
            year: y,
            violent_crime_rate_per_100k: bundle.localRatePer100k[i] ?? null,
            california_rate_per_100k: bundle.caRatePer100k[i] ?? null,
          });
        });
        if (bundle.latestLocalPercentile != null) {
          rows.push({
            location: bundle.locationDisplayName || location,
            year: bundle.latestYear,
            metric: 'crime_safety_risk_percentile',
            value: bundle.latestLocalPercentile,
            california_rate_per_100k: null,
            violent_crime_rate_per_100k: null,
          });
        }
        downloadDataAsCSV(rows, formatFilename(location, 'Crime_Safety_Data'));
      }}
      pngFilename={formatFilename(location, 'Crime_Safety_trend')}
      csvFilename={formatFilename(location, 'Crime_Safety_Data')}
    />
  );
};

export default CrimeSafetySection;
